import { Box, BoxProps, List, ListItem, ListItemButton, ListItemText, Stack } from "@mui/material";
import { useMemo, useState } from "react";

import AppIconButton from "@/common/components/Button/AppIconButton";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppModal from "@/common/components/Modals/AppModal";
import AppModalContent from "@/common/components/Modals/AppModalContent";
import AppModalTitle from "@/common/components/Modals/AppModalTitle";
import AppTypography from "@/common/components/Text/AppTypography";
import { ArrayHelper } from "@/common/helpers/array";
import { TextHelper } from "@/common/helpers/text";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { apiClient } from "@/core/api/ApiClient";
import { NameSuggestionDtoInRepairSpecNameSuggestionsDto } from "@/core/api/generated/v1";

import RepairSpecLink from "../RepairSpecLink";

interface Props {
  name: Nil<string>;
  repairSpecId?: Nil<string>;
  limit?: number;
  sx?: BoxProps["sx"];
  onSuggestionSelect: (newValue: NameSuggestionDtoInRepairSpecNameSuggestionsDto) => void;
}

export function RepairSpecNameInputSuggestions({
  name,
  repairSpecId,
  limit = 10,
  sx,
  onSuggestionSelect,
}: Props) {
  const shouldSuggest = !!name && name.length >= 3;

  const [repairSpecIdsToViewInModal, setRepairSpecIdsToViewInModal] = useState<Nil<string[]>>(null);

  const request = useApiRequest(
    apiClient.repairSpecsApi.apiV1RepairSpecsSuggestionsNameGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      limit: limit,
      search: name || undefined,
      excludeId: repairSpecId || undefined,
      excludeName: name || undefined,
    },
    {
      skip: !shouldSuggest,
      deps: [shouldSuggest, limit, repairSpecId],
      debouncedDeps: {
        deps: [name],
        wait: 1000,
        options: {
          leading: false,
          trailing: true,
        },
      },
    },
  );

  // instantly exclude just selected suggestion
  const suggestions = useMemo(
    () => request.data?.suggestions?.filter((x) => x.name !== name),
    [name, request.data?.suggestions],
  );
  const isAnySuggestion = ArrayHelper.isNotEmpty(suggestions);

  if ((!request.isLoading && !request.data) || !isAnySuggestion) {
    return null;
  }

  return (
    <Stack sx={sx}>
      {/* {request.isLoading && <TextLineListSkeleton itemCount={1} />} */}

      {suggestions && isAnySuggestion && (
        <Box>
          <AppTypography variant='body2'>Suggestions:</AppTypography>

          <List
            sx={{ maxHeight: 130, overflowY: "auto", scrollbarWidth: "thin" }}
            dense
            disablePadding
          >
            {suggestions?.map((suggestion, i) => (
              <ListItem
                key={i}
                sx={{ py: 0 }}
                dense
                disablePadding={false}
                disableGutters={false}
                secondaryAction={
                  <AppIconButton
                    size='extraSmall'
                    edge='end'
                    aria-label='view'
                    tooltipProps={{
                      title: "Quick view repair specs with this name",
                      enterDelay: 300,
                      enterNextDelay: 300,
                    }}
                    onClick={() => {
                      setRepairSpecIdsToViewInModal(suggestion.ids);
                    }}
                  >
                    <AppIcon of='list' />
                  </AppIconButton>
                }
              >
                <ListItemButton
                  sx={{ p: 0 }}
                  role={undefined}
                  dense
                  disableGutters
                  onClick={() => {
                    onSuggestionSelect(suggestion);
                    request.replaceData(null); // instantly hide suggestions after select
                  }}
                >
                  <ListItemText
                    primary={
                      <>
                        {suggestion.name}{" "}
                        {ArrayHelper.isNotEmpty(suggestion.ids) && (
                          <AppTypography component='span' variant='caption2'>
                            ({suggestion.ids?.length}{" "}
                            {TextHelper.pluralizeManual("spec", suggestion.ids?.length, "specs")})
                          </AppTypography>
                        )}
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* View repair specs modal */}
      {repairSpecIdsToViewInModal && (
        <AppModal open onClose={() => setRepairSpecIdsToViewInModal(null)} maxWidth='md' fullWidth>
          {(params) => (
            <>
              <AppModalTitle onClose={params.close}>
                {`Repair specs matching "${name}"`}
              </AppModalTitle>
              <AppModalContent>
                <List dense disablePadding>
                  {repairSpecIdsToViewInModal.map((id, i) => (
                    <ListItem key={i} sx={{ py: 0 }} dense disablePadding disableGutters>
                      <ListItemText
                        sx={{ m: 0 }}
                        primary={
                          <RepairSpecLink
                            entityId={id}
                            entity={undefined}
                            inlineProps={{
                              withActions: true,
                            }}
                          />
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AppModalContent>
            </>
          )}
        </AppModal>
      )}
    </Stack>
  );
}
